import {IFormulaNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {IFunctionResolver} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolver';
import {
    ClientFunction,
    FunctionArg,
    FunctionName,
    FunctionNamespace
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {INamingContext, ISheetAPI, ISheetValue, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Empty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class FunctionInvoker
{
    public static async invokeFunction(context:ISheetAPI,
                                       clientFunctionIO:IFunctionResolver,
                                       namingContext:INamingContext,
                                       namespace:FunctionNamespace,
                                       name:FunctionName,
                                       args:IFormulaNode[],
                                       argValues:(IValue | ISheetValue)[],
                                       javaEvalContext?:any):Promise<any>
    {
        const cf:ClientFunction = clientFunctionIO.findFunctionByName(namespace, name);
        const result = this._computeInvokeWithArgs(context, namingContext, cf, argValues);

        if( result.isError )
        {
            // Performance: Frame serialization is slow and has a hit on performance
            //mk.error(area.Formula, `[toValue] FormulaInvokeExpression.apply: ${cf.namespace}.${cf.name}(${FunctionInvoker._printValues(result.invokeArgs)}) => ${EvalErrors.InvalidFunctionArg}`);

            return Promise.resolve(EvalErrors.InvalidFunctionArg);
        }

        const invokeArgs = result.invokeArgs;

        return await this._invokeFunction(cf, invokeArgs, javaEvalContext);
    }

    @bind
    private static async _invokeFunction(cf:ClientFunction, coercedArgs:(IValue | ISheetValue)[], javaEvalContext?:any):Promise<any>
    {
        try
        {
            const resultOfFunction:any = await cf.invoke(coercedArgs, javaEvalContext);
            // Performance: Frame serialization is slow and has a hit on performance
            //mk.debug(area.Formula, `[toValue] FormulaInvokeExpression.apply: ${cf.namespace}.${cf.name}(${FunctionInvoker._printValues(coercedArgs)}) => ${resultOfFunction}`);

            return resultOfFunction;
        }
        catch( error )
        {
            console.info('_invokeFunction', error);
            // Performance: Frame serialization is slow and has a hit on performance
            //mk.error(area.Formula, `[toValue] FormulaInvokeExpression.apply: ${cf.namespace}.${cf.name}(${FunctionInvoker._printValues(coercedArgs)}) => ${error}`);
            const details = error instanceof Error ? error.message : '';
            return new AError('#FlowInvokationFailed', details);
        }
    }

    public static _printValues(values:(IValue | ISheetValue | ISheetAPI)[]):string
    {
        return values.map((v:IValue | ISheetValue | ISheetAPI) =>
        {
            if( Type.isInstanceOfIValue(v as any) )
                return `${(v as IValue).getType().kind}:${v.toString()}`;
            else
            {
                const anyValue = v as any;
                if( anyValue )
                    return `${anyValue.constructor.name}:${anyValue}`;
                else if( anyValue === undefined )
                    return `[undefined]`;
                else
                    return `[null]`;
            }

        }).join(', ');
    }

    @bind
    private static _computeInvokeWithArgs(context:ISheetAPI,
                                          namingContext:INamingContext,
                                          cf:ClientFunction,
                                          argValues:(IValue | ISheetValue)[]):{isError:boolean, invokeArgs:(IValue | ISheetValue)[]}
    {
        {
            const coerceResult = this._coerceArguments(context, namingContext, cf, argValues);
            if( coerceResult.isError )
                return {isError: true, invokeArgs: [EvalErrors.InvalidFunctionArg]};

            return {isError: false, invokeArgs: coerceResult.coercedArgs};
        }
    }

    @bind
    private static _coerceArguments(context:ISheetAPI,
                                    namingContext:INamingContext,
                                    cf:ClientFunction,
                                    argValues:(IValue | ISheetValue)[]):{isError:boolean, coercedArgs:any[]}
    {
        const coercedArgs:any[] = [];
        const declaredArgs = cf.args;

        let argValueIndex = 0;

        for( let i = 0; i < declaredArgs.length; i++ )
        {
            const declaredArg = declaredArgs[i];
            if( declaredArg.type.type === SheetType.SheetAPI )
            {
                coercedArgs.push(context);
            }
            else if( declaredArg.repeatable )
            {
                const repeatableArgCoercedValues:any[] = [];

                while( argValueIndex < argValues.length )
                {
                    const coerceArgumentResult = FunctionInvoker._coerceArgument(context, namingContext, argValues[argValueIndex], declaredArg);

                    //mk.debug(area.Formula, `[toValue] coerceArgument '${declaredArg.toString()}'=${FunctionInvoker._printValues([argValues[argValueIndex]])}`, coerceArgumentResult);

                    if( coerceArgumentResult.isError )
                        return {isError: true, coercedArgs: [EvalErrors.InvalidFunctionArg]};

                    repeatableArgCoercedValues.push(coerceArgumentResult.coercedArg);

                    if( coerceArgumentResult.usedArgValue )
                        argValueIndex++;
                }

                coercedArgs.push(repeatableArgCoercedValues);

                break;
            }
            else
            {
                if( argValueIndex < argValues.length )
                {
                    const argValue = argValues[argValueIndex];

                    const coerceArgumentResult = FunctionInvoker._coerceArgument(context, namingContext, argValue, declaredArg);

                    //mk.debug(area.Formula, `[toValue] coerceArgument '${declaredArg.toString()}'=${FunctionInvoker._printValues([argValue])}`, coerceArgumentResult);

                    if( coerceArgumentResult.isError )
                        return {isError: true, coercedArgs: [EvalErrors.InvalidFunctionArg]};

                    coercedArgs.push(coerceArgumentResult.coercedArg);

                    if( coerceArgumentResult.usedArgValue )
                        argValueIndex++;
                }
                else if( declaredArg.optional === true )
                {
                    coercedArgs.push(Empty);
                }
                else
                {
                    return {isError: true, coercedArgs: [EvalErrors.InvalidFunctionArg]};
                }
            }
        }

        return {isError: false, coercedArgs: coercedArgs};
    }

    private static _coerceArgument(context:ISheetAPI, namingContext:INamingContext, argValue:IValue | ISheetValue, declaredArg:FunctionArg):{isError:boolean; usedArgValue:boolean; coercedArg:any}
    {
        const declaredArgType = declaredArg.type;

        if( argValue === null )
        {
            if( declaredArg.optional )
                return {isError: false, usedArgValue: true, coercedArg: AEmpty.Value};
            else
            {
                mk.error(area.Formula, `[toValue] argument '${declaredArg.name}' is not optional`);
                return {isError: true, usedArgValue: false, coercedArg: EvalErrors.InvalidFunctionArg};
            }
        }
        else
        {
            const sheetType = declaredArgType.type;

            if( sheetType === SheetType.SheetLocation )
            {
                const sheetLocation = FormulaValueCoercionUtils.ConvertToSheetLocation(context.getSheetContext(), argValue);
                return {
                    isError: Type.isInstanceOf(sheetLocation, Type.Error),
                    usedArgValue: true,
                    coercedArg: sheetLocation
                };
            }
            else if( sheetType === SheetType.SheetRange )
            {
                const sheetRange = FormulaValueCoercionUtils.ConvertToSheetRange(context.getSheetContext(), argValue);
                return {isError: Type.isInstanceOf(sheetRange, Type.Error), usedArgValue: true, coercedArg: sheetRange};
            }
            else if( sheetType === SheetType.SheetRangeList )
            {
                const sheetRangeList = FormulaValueCoercionUtils.ConvertToSheetRangeList(context.getSheetContext(), argValue);
                return {
                    isError: Type.isInstanceOf(sheetRangeList, Type.Error),
                    usedArgValue: true,
                    coercedArg: sheetRangeList
                };
            }
            else if( sheetType === SheetType.AnyIValue )
            {
                const iValue = FormulaValueCoercionUtils.ConvertToIValue(context.getSheetContext(), namingContext, argValue);
                return {isError: false, usedArgValue: true, coercedArg: iValue};
            }
            else if( sheetType === SheetType.FormulaExpression )
            {
                return {isError: false, usedArgValue: true, coercedArg: argValue};
            }
            else if( sheetType === SheetType.Any )
            {
                return {isError: false, usedArgValue: true, coercedArg: argValue};
            }
            else
            {
                const regularType = declaredArgType.type as Type;
                if( regularType == null )
                {
                    mk.error(area.Formula, `[toValue] FormulaInvokeExpression: argument '${declaredArg.name}' has invalid type '${declaredArgType}'`);
                    return {isError: true, usedArgValue: false, coercedArg: EvalErrors.InvalidFunctionArg};
                }
                else
                {
                    const iValue = FormulaValueCoercionUtils.ConvertToIValue(context.getSheetContext(), namingContext, argValue);
                    if( !iValue )
                    {
                        return {isError: true, usedArgValue: true, coercedArg: EvalErrors.InvalidFunctionArg};
                    }
                    else
                    {
                        const listValues = iValue as IValue[];
                        if( Array.isArray(iValue) && listValues )
                        {
                            let result = [];

                            for( let i = 0; i < listValues.length; i++ )
                            {
                                const item = listValues[i];

                                if( !item )
                                {
                                    return {
                                        isError: true,
                                        usedArgValue: true,
                                        coercedArg: EvalErrors.InvalidFunctionArg
                                    };
                                }

                                if( Type.isInstanceOf(item, Type.Error) )
                                    return {isError: true, usedArgValue: true, coercedArg: iValue};
                                // else if( !item.isFrame )
                                else
                                {
                                    result[i] = CoercionUtils.coerce(item, regularType, null);
                                }
                                // else
                                // {
                                //     result[i] = item;
                                // }
                            }

                            return {isError: false, usedArgValue: true, coercedArg: result};
                        }
                        else
                        {
                            const singleValue = iValue as IValue;
                            if( !singleValue )
                            {
                                return {isError: true, usedArgValue: true, coercedArg: EvalErrors.InvalidFunctionArg};
                            }

                            if( Type.isInstanceOf(singleValue, Type.Error) )
                                return {isError: true, usedArgValue: true, coercedArg: iValue};
                            // else if( !singleValue.isFrame )
                            else
                            {
                                const coercedValue = CoercionUtils.coerce(singleValue, regularType, null);
                                return {isError: false, usedArgValue: true, coercedArg: coercedValue};
                            }
                            // else
                            // {
                            //     return {isError: false, usedArgValue: true, coercedArg: iValue};
                            // }
                        }
                    }
                }
            }
        }

    }
}
